import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import { CheckboxField, SelectInput, TextInput } from '../../components/common/form';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { getClassroomSettings, saveClassroomSettings } from '../../api/classroom';
import { getApiErrorInfo } from '../../utils/apiError';

/**
 * Academy classroom defaults (admin) — persisted on server.
 */
export default function ClassroomSettingsPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const s = await getClassroomSettings();
      setForm(s);
      try {
        localStorage.removeItem('classroom.settings.draft');
      } catch {
        /* ignore */
      }
    } catch (err) {
      setForm(null);
      setError(getApiErrorInfo(err, t('classroom.settings.loadFailed')).message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const isValidEmail = (value) =>
    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(String(value || '').trim());

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form) return;
    setSaving(true);
    setError('');
    setNotice('');
    const host = String(form.hostUserEmail || '').trim();
    if (host && !isValidEmail(host)) {
      setError(t('classroom.settings.hostEmailInvalid'));
      setSaving(false);
      return;
    }
    try {
      const saved = await saveClassroomSettings({
        ...form,
        hostUserEmail: host || null,
      });
      setForm(saved);
      setNotice(t('classroom.settings.saveSuccess'));
    } catch (err) {
      setError(getApiErrorInfo(err, t('classroom.settings.saveFailed')).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !form) {
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('classroom.settings.title')}
        subtitle={t('classroom.settings.subtitle')}
        backTo="/admin/classroom"
      />
    );
  }

  if (!form) {
    return (
      <div className="page-shell-form max-w-3xl">
        <PageHeaderSection
          eyebrow={t('nav.classroom')}
          title={t('classroom.settings.title')}
          subtitle={t('classroom.settings.subtitle')}
          showBack
          backTo="/admin/classroom"
        />
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          {error || t('classroom.settings.loadFailed')}
        </div>
        <Button type="button" variant="page-primary" onClick={loadSettings}>
          {t('classroom.settings.retryLoad')}
        </Button>
      </div>
    );
  }

  return (
    <div className="page-shell-form max-w-3xl">
      <PageHeaderSection
        eyebrow={t('nav.classroom')}
        title={t('classroom.settings.title')}
        subtitle={t('classroom.settings.subtitle')}
        showBack
        backTo="/admin/classroom"
      />

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        {t('classroom.settings.infoBannerLive')}
      </div>

      {notice && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" role="status">
          {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={save} noValidate className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <SelectInput
          label={t('classroom.settings.defaultProvider')}
          value={form.defaultProvider || 'ZOOM'}
          onChange={(e) => setField('defaultProvider', e.target.value)}
          note={t('classroom.settings.defaultProviderNote')}
        >
          <option value="ZOOM">Zoom</option>
          <option value="EXTERNAL_LINK">{t('classroom.settings.providerExternalLink')}</option>
        </SelectInput>

        <TextInput
          label={t('classroom.settings.hostUserEmail')}
          name="classroomHostUserEmail"
          type="text"
          inputMode="email"
          value={form.hostUserEmail || ''}
          onChange={(e) => setField('hostUserEmail', e.target.value)}
          note={t('classroom.settings.hostUserEmailNote')}
          placeholder="host@academy.com"
          autoComplete="off"
        />

        <CheckboxField
          layout="toggle-card"
          label={t('classroom.settings.waitingRoom')}
          description={t('classroom.settings.waitingRoomNote')}
          checked={!!form.waitingRoom}
          onChange={(e) => setField('waitingRoom', e.target.checked)}
        />
        <CheckboxField
          layout="toggle-card"
          label={t('classroom.settings.muteOnEntry')}
          checked={!!form.muteOnEntry}
          onChange={(e) => setField('muteOnEntry', e.target.checked)}
        />
        <CheckboxField
          layout="toggle-card"
          label={t('classroom.settings.allowStudentCameraDefault')}
          description={t('classroom.settings.allowStudentCameraNote')}
          checked={!!form.allowStudentCameraDefault}
          onChange={(e) => setField('allowStudentCameraDefault', e.target.checked)}
        />
        <CheckboxField
          layout="toggle-card"
          label={t('classroom.settings.notifyOnStart')}
          description={t('classroom.settings.notifyOnStartNote')}
          checked={!!form.notifyOnStart}
          onChange={(e) => setField('notifyOnStart', e.target.checked)}
        />

        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" variant="page-primary" disabled={saving}>
            {saving ? t('classroom.settings.saving') : t('classroom.settings.save')}
          </Button>
          <Link
            to="/admin/classroom/zoom"
            className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            {t('classroom.settings.gotoZoom')}
          </Link>
        </div>
      </form>
    </div>
  );
}
